import React from 'react';
import { InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';

export const MCA_STACK_TIER_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

export const MCA_REFI_TIER_MIN = 1;
export const MCA_REFI_TIER_MAX = 99;

type McaStackLevelFieldProps = {
  stackLevel: number | null;
  reverseFunding: boolean;
  onChange: (next: { stackLevel: number | null; reverseFunding: boolean }) => void;
};

export function McaStackLevelField({ stackLevel, reverseFunding, onChange }: McaStackLevelFieldProps) {
  const selectValue = reverseFunding ? 'V' : stackLevel == null ? '' : String(stackLevel);

  return (
    <Stack alignItems="center" sx={{ paddingLeft: 0.5, paddingBottom: 2, paddingRight: 1 }}>
      <InputLabel htmlFor="mac-entity-stackLevel">
        Stack
      </InputLabel>
      <Select
        id="mac-entity-stackLevel"
        size="small"
        displayEmpty
        value={selectValue}
        onChange={(e) => {
          const raw = e.target.value as string;
          if (raw === '') onChange({ stackLevel: null, reverseFunding: false });
          else if (raw === 'V') onChange({ stackLevel: null, reverseFunding: true });
          else onChange({ stackLevel: Number(raw), reverseFunding: false });
        }}
        renderValue={(value) => {
          if (value === '') return '-';
          if (value === 'V') return 'V';
          return `S${value}`;
        }}
        sx={{ width: 65 }}
      >
        <MenuItem value="">-</MenuItem>
        <MenuItem value="V">V</MenuItem>
        {MCA_STACK_TIER_OPTIONS.map((n) => (
          <MenuItem key={n} value={String(n)}>
            {`S${n}`}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}

type McaRefiLevelFieldProps = {
  refiLevel: number | null;
  onChange: (level: number | null) => void;
  onBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error: boolean;
  helperText: React.ReactNode;
};

export function McaRefiLevelField({ refiLevel, onChange, onBlur, error, helperText }: McaRefiLevelFieldProps) {
  return (
    <Stack alignItems="center" sx={{ paddingLeft: 1.5, paddingRight: 0.5, paddingBottom: 2 }}>
      <InputLabel htmlFor="mac-entity-refiLevel">REFI #</InputLabel>
      <TextField
        size="small"
        id="mac-entity-refiLevel"
        placeholder="1–99"
        name="refiLevel"
        type="number"
        value={refiLevel == null ? '' : String(refiLevel)}
        onBlur={onBlur}
        onChange={(e) => {
          const parsed = parseInt(e.target.value, 10);
          onChange(
            Number.isNaN(parsed) ? null : Math.min(MCA_REFI_TIER_MAX, Math.max(MCA_REFI_TIER_MIN, parsed))
          );
        }}
        error={error}
        helperText={helperText}
        InputProps={{
          inputProps: { min: MCA_REFI_TIER_MIN, max: MCA_REFI_TIER_MAX, step: 1 }
        }}
        sx={{ width: 65 }}
      />
    </Stack>
  );
}
